import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  FileText,
  Sparkles,
  CheckCircle2,
  Trash2,
} from "lucide-react";
import logo from "@/assets/logo_medium.png";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingProgress } from "@/components/OnboardingProgress";
import { OnboardingFooter } from "@/components/OnboardingFooter";
import ErrorBanner from "@/components/ErrorBanner";
import { AppError, toAppError } from "@/lib/errors";
import { sendOnboardingCompleteEmails } from "@/lib/sendOnboardingCompleteEmails";

interface DocumentContent {
  id: string;
  file_name: string;
  file_path: string;
  extracted_text: string;
  isEdited: boolean;
}

const Analysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = location.state?.patientId;

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<DocumentContent[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [structuredJson, setStructuredJson] = useState<any>(null);
  const [jsonString, setJsonString] = useState("");
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    // DEPRECATED: Este fluxo está sendo migrado para /onboarding/miafile
    toast.info("⚠️ Este fluxo está descontinuado. Use /onboarding/miafile", {
      duration: 3000,
    });
    setTimeout(() => navigate("/onboarding/miafile"), 3000);
  }, [navigate]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: docs, error: fetchError } = await supabase
        .from("documents_onboarding")
        .select("id, file_name, file_path, extracted_text")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: true });

      if (fetchError) throw fetchError;

      // Map documents with their text
      const docsWithText = (docs || []).map((doc) => ({
        id: doc.id,
        file_name: doc.file_name,
        file_path: doc.file_path,
        extracted_text:
          doc.extracted_text || `[Texto não extraído de ${doc.file_name}]`,
        isEdited: false,
      }));

      setDocuments(docsWithText);
    } catch (err) {
      console.error("Erro ao carregar documentos:", err);
      setError(new AppError("EDGE_500", "Erro ao carregar documentos"));
      toast.error("Erro ao carregar documentos");
    } finally {
      setLoading(false);
    }
  };

  const handleTextEdit = (docId: string, newText: string) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === docId
          ? { ...doc, extracted_text: newText, isEdited: true }
          : doc,
      ),
    );
  };

  const handleDeleteDocument = async (
    docId: string,
    filePath: string,
    fileName: string,
  ) => {
    try {
      setError(null);

      if (!confirm(`Deseja realmente excluir "${fileName}"?`)) {
        return;
      }

      // Deletar do banco
      const { error: dbError } = await supabase
        .from("documents_onboarding")
        .delete()
        .eq("id", docId);

      if (dbError) throw new AppError("DB_UPDATE", dbError.message);

      // Deletar do storage
      const { error: storageError } = await supabase.storage
        .from("patient-docs")
        .remove([filePath]);

      if (storageError) {
        console.warn("Erro ao deletar arquivo do storage:", storageError);
      }

      // Atualizar estado local
      setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
      toast.success(`${fileName} excluído com sucesso`);
    } catch (err) {
      console.error("Erro ao excluir documento:", err);
      setError(toAppError(err));
      toast.error("Erro ao excluir documento");
    }
  };

  const generateStructure = async () => {
    const timeoutId = setTimeout(() => {
      toast.error("Tempo esgotado. Tente novamente com menos texto.");
      setIsGenerating(false);
    }, 120000); // 2 minutos de timeout

    let progressInterval: NodeJS.Timeout | null = null;

    try {
      setIsGenerating(true);
      setError(null);

      // Consolidate all edited texts
      const consolidatedText = documents
        .map((doc) => `# ${doc.file_name}\n${doc.extracted_text}`)
        .join("\n\n---\n\n");

      if (!consolidatedText.trim()) {
        toast.error("Nenhum conteúdo para analisar");
        return;
      }

      // Validar tamanho do texto
      if (consolidatedText.length > 50000) {
        toast.warning(
          "Texto muito longo. Pode levar até 2 minutos para processar.",
        );
      }

      // Get patient context
      const { data: patient } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", patientId)
        .single();

      // Atualizar toast a cada 10 segundos
      toast.loading("Processando com IA... Aguarde.", { id: "ai-progress" });
      progressInterval = setInterval(() => {
        toast.loading("Ainda processando com IA... Aguarde.", {
          id: "ai-progress",
        });
      }, 10000);

      const { data, error } = await supabase.functions.invoke(
        "validate-clinical-data",
        {
          body: {
            action: "propose",
            editedLabText: consolidatedText,
            patientContext: patient || {},
          },
        },
      );

      clearInterval(progressInterval);
      clearTimeout(timeoutId);
      toast.dismiss("ai-progress");

      if (error) throw error;

      setStructuredJson(data);
      setJsonString(JSON.stringify(data, null, 2));
      toast.success("Estrutura gerada com sucesso!");
    } catch (err: any) {
      if (progressInterval) clearInterval(progressInterval);
      clearTimeout(timeoutId);
      toast.dismiss("ai-progress");

      console.error("Erro ao gerar estrutura:", err);

      if (
        err.message?.includes("FunctionsFetchError") ||
        err.message?.includes("timeout")
      ) {
        setError(new AppError("EDGE_NETWORK", "Erro de conexão ou timeout"));
        toast.error(
          "Erro de conexão ou timeout. Verifique sua internet e tente novamente.",
        );
      } else {
        setError(new AppError("AI_FAILED", "Erro ao gerar estrutura via IA"));
        toast.error("Erro ao gerar estrutura via IA");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleJsonEdit = (value: string) => {
    setJsonString(value);
    try {
      const parsed = JSON.parse(value);
      setStructuredJson(parsed);
    } catch {
      // Invalid JSON, don't update structuredJson
    }
  };

  const saveValidated = async () => {
    try {
      setIsSaving(true);
      setError(null);

      if (!structuredJson) {
        toast.error("Gere a estrutura antes de salvar");
        return;
      }

      // Validate JSON
      try {
        JSON.parse(jsonString);
      } catch {
        toast.error("JSON inválido. Corrija antes de salvar.");
        return;
      }

      const consolidatedText = documents
        .map((doc) => `# ${doc.file_name}\n${doc.extracted_text}`)
        .join("\n\n---\n\n");

      const { error: saveError } = await supabase.functions.invoke(
        "validate-clinical-data",
        {
          body: {
            action: "save",
            patientId,
            editedLabText: consolidatedText,
            structuredJson,
            useOnboardingTables: true,
          },
        },
      );

      if (saveError) throw saveError;

      // Mark onboarding as complete
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from("profiles")
          .update({
            onboarding_completed: true,
            onboarding_step: 4,
          })
          .eq("id", user.id);
        await sendOnboardingCompleteEmails();
      }

      toast.success("Dados validados e salvos com sucesso!");
      navigate("/app");
    } catch (err) {
      console.error("Erro ao salvar:", err);
      setError(new AppError("DB_UPDATE", "Erro ao salvar dados validados"));
      toast.error("Erro ao salvar dados");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <OnboardingProgress currentStep={3} totalSteps={3} />

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Validação Clínica
              </h1>
              <p className="text-muted-foreground">
                Revise e edite o conteúdo extraído dos documentos antes da
                análise
              </p>
            </div>

            {error && (
              <div className="mb-6">
                <ErrorBanner error={error} />
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">
                  Carregando documentos...
                </p>
              </div>
            ) : (
              <>
                {/* Document text areas */}
                <div className="space-y-6 mb-6">
                  {documents.map((doc) => (
                    <div key={doc.id} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <FileText className="w-4 h-4 text-secondary" />
                          <label className="text-sm font-medium">
                            {doc.file_name}
                          </label>
                          {doc.isEdited && (
                            <span className="text-xs text-muted-foreground">
                              (editado)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() =>
                            handleDeleteDocument(
                              doc.id,
                              doc.file_path,
                              doc.file_name,
                            )
                          }
                          className="p-2 rounded-md hover:bg-destructive/10 text-destructive transition"
                          title="Excluir documento"
                          type="button"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <Textarea
                        value={doc.extracted_text}
                        onChange={(e) => handleTextEdit(doc.id, e.target.value)}
                        placeholder="Conteúdo extraído do documento..."
                        className="min-h-[200px] font-mono text-sm"
                      />
                    </div>
                  ))}
                </div>

                {/* Generate structure button */}
                {!structuredJson && (
                  <Button
                    onClick={generateStructure}
                    disabled={isGenerating || documents.length === 0}
                    className="w-full mb-6"
                    size="lg"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gerando estrutura...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Gerar estrutura via IA
                      </>
                    )}
                  </Button>
                )}

                {/* Structured JSON editor */}
                {structuredJson && (
                  <div className="space-y-4 mb-6 p-6 bg-muted/30 rounded-lg border border-muted">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-secondary" />
                      <h3 className="font-semibold">Estrutura Gerada</h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Resumo</h4>
                        <p className="text-sm text-muted-foreground bg-background p-3 rounded">
                          {structuredJson.analysis?.summary || "N/A"}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          Nível de Risco
                        </h4>
                        <span
                          className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                            structuredJson.analysis?.risk_level === "alto"
                              ? "bg-destructive/10 text-destructive"
                              : structuredJson.analysis?.risk_level ===
                                "moderado"
                              ? "bg-yellow-500/10 text-yellow-600"
                              : "bg-green-500/10 text-green-600"
                          }`}
                        >
                          {structuredJson.analysis?.risk_level || "N/A"}
                        </span>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          JSON Editável
                        </h4>
                        <Textarea
                          value={jsonString}
                          onChange={(e) => handleJsonEdit(e.target.value)}
                          className="font-mono text-xs min-h-[300px]"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={saveValidated}
                      disabled={isSaving}
                      className="w-full"
                      size="lg"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Aprovar e finalizar cadastro
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <OnboardingFooter />
      </div>
    </div>
  );
};

export default Analysis;
